import React, { useState, useEffect, useCallback } from "react";
import { categories } from "../../data/carData";

const FilterSidebar = ({ filters, setFilters }) => {
  const [isDragging, setIsDragging] = useState(null);

  const calculateValue = useCallback((position, width) => {
    const percentage = Math.max(0, Math.min(1, position / width));
    const value = Math.round(percentage * (390 - 20) + 20);
    return Math.min(Math.max(20, value), 390);
  }, []);

  const handlePriceChange = useCallback(
    (clientX, container, handle) => {
      const rect = container.getBoundingClientRect();
      const position = clientX - rect.left;
      const value = calculateValue(position, rect.width);

      setFilters((prev) => {
        const [min, max] = prev.priceRange;
        let newRange;

        if (handle === "min") {
          // Ensure min doesn't exceed max - 10
          const newMin = Math.min(value, max - 10);
          newRange = [newMin, max];
        } else {
          // Ensure max doesn't go below min + 10
          const newMax = Math.max(value, min + 10);
          newRange = [min, newMax];
        }

        return {
          ...prev,
          priceRange: newRange,
        };
      });
    },
    [setFilters, calculateValue]
  );

  const handleMove = useCallback(
    (e) => {
      if (!isDragging) return;

      const clientX = e.type.includes("touch")
        ? e.touches[0].clientX
        : e.clientX;

      const container = document.getElementById("price-range-track");
      if (container) {
        handlePriceChange(clientX, container, isDragging);
        e.preventDefault(); // Prevent text selection while dragging
      }
    },
    [isDragging, handlePriceChange]
  );

  const handleEnd = useCallback(() => {
    setIsDragging(null);
    document.body.style.cursor = "default";
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.body.style.cursor = "grabbing";
      window.addEventListener("mousemove", handleMove, { passive: false });
      window.addEventListener("mouseup", handleEnd);
      window.addEventListener("touchmove", handleMove, { passive: false });
      window.addEventListener("touchend", handleEnd);
    }

    return () => {
      document.body.style.cursor = "default";
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, handleMove, handleEnd]);

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 Aleo">
      {/* Price Range */}
      <div className="mb-8">
        <h3 className="font-bold text-lg mb-4">Price Range (per day)</h3>
        <div className="relative h-8 mt-8">
          <div
            id="price-range-track"
            className="absolute w-full h-2 bg-gray-200 rounded cursor-pointer"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const clickPosition = e.clientX - rect.left;
              const value = calculateValue(clickPosition, rect.width);

              const [min, max] = filters.priceRange;
              const distToMin = Math.abs(value - min);
              const distToMax = Math.abs(value - max);

              handlePriceChange(
                e.clientX,
                e.currentTarget,
                distToMin < distToMax ? "min" : "max"
              );
            }}
          >
            {/* Price range track fill */}
            <div
              className="absolute h-full bg-red-500 rounded"
              style={{
                left: `${((filters.priceRange[0] - 20) / (390 - 20)) * 100}%`,
                right: `${
                  100 - ((filters.priceRange[1] - 20) / (390 - 20)) * 100
                }%`,
              }}
            />

            {/* Price range handles */}
            {["min", "max"].map((handle) => (
              <div
                key={handle}
                className={`absolute w-7 h-7 -mt-2.5 -ml-3.5 bg-red-500 rounded-full shadow-lg cursor-grab
                  active:cursor-grabbing transform transition-transform duration-150
                  ${
                    isDragging === handle
                      ? "scale-110"
                      : "hover:scale-105"
                  }`}
                style={{
                  left: `${
                    ((filters.priceRange[handle === "min" ? 0 : 1] - 20) /
                      (390 - 20)) *
                    100
                  }%`,
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsDragging(handle);
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  setIsDragging(handle);
                }}
              />
            ))}
          </div>
        </div>
        <div className="flex justify-between text-sm text-gray-600">
          <span>€{Math.round(filters.priceRange[0])}</span>
          <span>€{Math.round(filters.priceRange[1])}</span>
        </div>
      </div>

      <div className="h-px bg-gray-200 my-6" />

      {/* Car Categories */}
      {!filters.category.includes("Jeep Wrangler") && (
        <div className="mb-8">
          <h3 className="font-bold text-lg mb-4">Car Category</h3>
          <div className="space-y-3">
            {categories.map((category) => (
              <label
                key={category}
                className="flex items-center cursor-pointer group hover:bg-gray-50 p-2 rounded-md transition-colors"
              >
                <div className="relative w-7 h-7 mr-3">
                  <input
                    type="checkbox"
                    className="peer hidden"
                    checked={filters.category.includes(category)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFilters((prev) => ({
                          ...prev,
                          category: [...prev.category, category],
                        }));
                      } else {
                        setFilters((prev) => ({
                          ...prev,
                          category: prev.category.filter(
                            (cat) => cat !== category
                          ),
                        }));
                      }
                    }}
                  />
                  <div className="w-7 h-7 border-2 border-gray-300 rounded transition-all duration-200 peer-checked:border-red-500 peer-checked:bg-red-500 group-hover:border-red-400 group-hover:shadow-md" />
                  <div className="absolute top-1.5 left-1.5 w-4 h-4 text-white opacity-0 peer-checked:opacity-100 transition-all duration-200 scale-0 peer-checked:scale-100">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                </div>
                <span className="text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                  {category}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="h-px bg-gray-200 my-6" />

      {/* Passengers */}
      <div className="mb-8">
        <h3 className="font-bold text-lg mb-4">Passengers</h3>
        <div className="space-y-3">
          {[
            "0-2 Passengers",
            "3-5 Passengers",
            "6-7 Passengers",
          ].map((option) => (
            <label
              key={option}
              className="flex items-center cursor-pointer group hover:bg-gray-50 p-2 rounded-md transition-colors"
            >
              <div className="relative w-7 h-7 mr-3">
                <input
                  type="radio"
                  name="passengers"
                  className="peer hidden"
                  checked={filters.passengers === option}
                  onChange={() =>
                    setFilters((prev) => ({ ...prev, passengers: option }))
                  }
                />
                <div className="w-7 h-7 border-2 border-gray-300 rounded-full transition-all duration-200 peer-checked:border-red-500 group-hover:border-red-400 group-hover:shadow-md" />
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-red-500 rounded-full opacity-0 peer-checked:opacity-100 transition-all duration-200 scale-0 peer-checked:scale-100" />
              </div>
              <span className="text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                {option}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="h-px bg-gray-200 my-6" />

      {/* Transmission */}
      <div className="mb-4">
        <h3 className="font-bold text-lg mb-4">Transmission</h3>
        <div className="space-y-3">
          {["Manual", "Automatic"].map((option) => (
            <label
              key={option}
              className="flex items-center cursor-pointer group hover:bg-gray-50 p-2 rounded-md transition-colors"
            >
              <div className="relative w-7 h-7 mr-3">
                <input
                  type="radio"
                  name="transmission"
                  className="peer hidden"
                  checked={filters.transmission === option}
                  onChange={() =>
                    setFilters((prev) => ({ ...prev, transmission: option }))
                  }
                />
                <div className="w-7 h-7 border-2 border-gray-300 rounded-full transition-all duration-200 peer-checked:border-red-500 group-hover:border-red-400 group-hover:shadow-md" />
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-red-500 rounded-full opacity-0 peer-checked:opacity-100 transition-all duration-200 scale-0 peer-checked:scale-100" />
              </div>
              <span className="text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                {option}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;
